import FilePreviewComponent from "@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue";
import {
  GET_ENTITY_COMPLIANCE_OVERVIEW_URL,
  GET_RULE_QUESTIONS_ANSWERS_URL,
  SYNC_APPLICATION_ANSWERS_TO_COMPLIANCE_URL,
  get_headers,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from "axios";

const FILE_QUESTION_TYPES = ["file", "facial_verification"];
const MULTISELECT_QUESTION_TYPES = ["multi_select", "single_select"];

const ANSWER_SOURCE_CLASSES = {
  compliance: "manually-verified",
  application: "pending",
  none: "unanswered",
};

const ANSWER_SOURCE_LABELS = {
  compliance: "Compliant",
  application: "Imported",
  none: "Unanswered",
};

const ANSWER_SOURCE_ICONS = {
  compliance: "bi bi-check-circle-fill",   // ✓ solid tick
  application: "bi bi-clock-fill",          // ◑ clock — imported / pending
  none: "",
};

export default {
  name: "EntityComplianceAnswers",
  mixins: [alert_mixin],
  components: { FilePreviewComponent },

  props: {
    entity_profile_id: {
      type: [String, Number],
      required: true,
    },
  },

  emits: ["data-loaded"],

  data: () => ({
    headers: get_headers(),
    overview_data: null,
    is_loading_overview: false,
    is_syncing: false,
    search_query: "",
    collapsed_sub_sections: {},
    expanded_rules: {},
    rule_details: {},
    loading_rule_details: {},
    show_file_preview: false,
    preview_file_url: "",
    preview_file_name: "",
  }),

  computed: {
    has_data() {
      const has_overview = this.overview_data !== null;
      return has_overview;
    },

    total_rules() {
      const count = Reflect.get(this.overview_data || {}, "total_rules") || 0;
      return count;
    },

    total_questions() {
      const count = Reflect.get(this.overview_data || {}, "total_questions") || 0;
      return count;
    },

    total_answered() {
      const count = Reflect.get(this.overview_data || {}, "total_answered") || 0;
      return count;
    },

    total_unanswered() {
      const count = Reflect.get(this.overview_data || {}, "total_unanswered") || 0;
      return count;
    },

    completion_percentage() {
      if (this.total_questions === 0) return 0;
      return Math.round((this.total_answered / this.total_questions) * 100);
    },

    rules_list() {
      const rules = Reflect.get(this.overview_data || {}, "rules") || [];
      return rules;
    },

    filtered_rules() {
      const search_term = this.search_query.toLowerCase().trim();
      if (!search_term) {
        return this.rules_list;
      }

      return this.rules_list.filter((rule_item) => {
        const rule_name = rule_item.name || "";
        const rule_description = rule_item.description || "";
        return rule_name.toLowerCase().includes(search_term) ||
               rule_description.toLowerCase().includes(search_term);
      });
    },

    entity_name() {
      const name = Reflect.get(this.overview_data || {}, "entity_name") || "Entity";
      return name;
    },

    entity_type_name() {
      const type_name = Reflect.get(this.overview_data || {}, "entity_type_name") || "";
      return type_name;
    },
  },

  watch: {
    entity_profile_id: {
      immediate: true,
      handler(new_id) {
        if (new_id) {
          this.load_compliance_overview();
        }
      },
    },
  },

  methods: {
    // ─── Data Loading ────────────────────────────────────────────

    load_compliance_overview() {
      if (!this.entity_profile_id) {
        this.overview_data = null;
        return;
      }

      this.is_loading_overview = true;
      const request_params = { entity_profile_id: this.entity_profile_id };

      axios
        .get(GET_ENTITY_COMPLIANCE_OVERVIEW_URL, {
          headers: this.headers,
          params: request_params,
        })
        .then((response) => {
          this.handle_overview_response(response);
        })
        .catch((error) => {
          this.handle_overview_error(error);
        })
        .finally(() => {
          this.is_loading_overview = false;
        });
    },

    handle_overview_response(response) {
      if (response.data.status === "success") {
        this.overview_data = response.data.data;
        this.$emit("data-loaded", this.total_questions > 0);
      } else {
        this.overview_data = null;
        this.$emit("data-loaded", false);
      }
    },

    handle_overview_error(error) {
      this.show_error("Failed to load compliance data: " + (error.message || "Unknown error"));
      this.overview_data = null;
      this.$emit("data-loaded", false);
    },

    load_rule_details(rule_id) {
      if (!this.entity_profile_id || !rule_id) return;

      Reflect.set(this.loading_rule_details, rule_id, true);
      const request_params = {
        compliance_rule_id: rule_id,
        entity_profile_id: this.entity_profile_id,
      };

      axios
        .get(GET_RULE_QUESTIONS_ANSWERS_URL, {
          headers: this.headers,
          params: request_params,
        })
        .then((response) => {
          this.handle_rule_details_response(response, rule_id);
        })
        .catch((error) => {
          this.handle_rule_details_error(error, rule_id);
        })
        .finally(() => {
          Reflect.set(this.loading_rule_details, rule_id, false);
        });
    },

    handle_rule_details_response(response, rule_id) {
      const response_status = Reflect.get(response.data, "status");
      if (response_status === "success") {
        const response_data = Reflect.get(response.data, "data");
        Reflect.set(this.rule_details, rule_id, response_data);
      }
    },

    handle_rule_details_error(error, rule_id) {
      this.show_error("Failed to load rule details: " + (error.message || "Unknown error"));
      Reflect.set(this.rule_details, rule_id, null);
    },

    // ─── Sync ────────────────────────────────────────────────────

    sync_answers() {
      if (!this.entity_profile_id) return;

      this.is_syncing = true;
      const request_payload = { entity_profile_id: this.entity_profile_id };

      axios
        .post(SYNC_APPLICATION_ANSWERS_TO_COMPLIANCE_URL, request_payload, {
          headers: this.headers,
        })
        .then((response) => {
          this.handle_sync_response(response);
        })
        .catch((error) => {
          this.handle_sync_error(error);
        })
        .finally(() => {
          this.is_syncing = false;
        });
    },

    handle_sync_response(response) {
      if (response.data.status === "success") {
        const synced_count = response.data.data.synced_count;
        this.show_success(`Synced ${synced_count} answers from application forms`);
        this.load_compliance_overview();
      } else {
        this.show_error(response.data.message || "Failed to sync");
      }
    },

    handle_sync_error(error) {
      this.show_error("Failed to sync answers: " + (error.message || "Unknown error"));
    },

    // ─── Rule / Sub-Section Expand & Collapse ────────────────────

    toggle_rule_expand(rule_id) {
      const is_expanded = Reflect.get(this.expanded_rules, rule_id) || false;
      const new_value = !is_expanded;
      Reflect.set(this.expanded_rules, rule_id, new_value);

      if (new_value) {
        const has_details = Reflect.get(this.rule_details, rule_id);
        if (!has_details) {
          this.load_rule_details(rule_id);
        }
      }
    },

    is_rule_expanded(rule_id) {
      const is_expanded = Reflect.get(this.expanded_rules, rule_id) || false;
      return is_expanded;
    },

    is_rule_loading(rule_id) {
      const is_loading = Reflect.get(this.loading_rule_details, rule_id) || false;
      return is_loading;
    },

    get_rule_details(rule_id) {
      const details = Reflect.get(this.rule_details, rule_id);
      return details;
    },

    toggle_sub_section_collapse(sub_section_key) {
      const current_value = Reflect.get(this.collapsed_sub_sections, sub_section_key) || false;
      const new_value = !current_value;
      Reflect.set(this.collapsed_sub_sections, sub_section_key, new_value);
    },

    is_sub_section_collapsed(sub_section_key) {
      return Reflect.get(this.collapsed_sub_sections, sub_section_key) || false;
    },

    get_all_sub_sections_for_rule(rule_id) {
      
      const rule_data = Reflect.get(this.rule_details, rule_id);
      if (!rule_data) return [];

      const main_sections = rule_data.main_sections || [];
      const all_sub_sections = [];

      main_sections.forEach((main_section) => {
        const sub_sections = main_section.sub_sections || [];
        all_sub_sections.push(...sub_sections);
      });

      return all_sub_sections;
    },

    // ─── Badge & Progress Helpers ────────────────────────────────

    get_answer_source_badge_class(answer_source) {
      const badge_class = Reflect.get(ANSWER_SOURCE_CLASSES, answer_source) || "bg-secondary";
      return badge_class;
    },

    get_answer_source_label(answer_source) {
      const label = Reflect.get(ANSWER_SOURCE_LABELS, answer_source) || "Unknown";
      return label;
    },

    get_answer_source_icon(answer_source) {
      return Reflect.get(ANSWER_SOURCE_ICONS, answer_source) || ANSWER_SOURCE_ICONS.none;
    },

    /**
     * Returns the CSS class for a progress bar based on its percentage.
     * Used for both the global bar and each rule's mini bar.
     */
    get_progress_color_class(percentage) {
      if (percentage >= 80) return "progress-high";
      if (percentage >= 50) return "progress-mid";
      return "progress-low";
    },

    /**
     * Returns just the width style object for a progress bar.
     * Keeps the percentage calculation out of the template.
     */
    get_progress_bar_width_style(answered, total) {
      const width = total > 0 ? (answered / total) * 100 : 0;
      const style_object = { width: `${width}%` };
      return style_object;
    },

    // ─── Question Type Checks ────────────────────────────────────

    is_file_type(question_type_raw) {
      const type_lower = (question_type_raw || "").toLowerCase();
      return FILE_QUESTION_TYPES.includes(type_lower);
    },

    is_multiselect_type(question_type_raw) {
      const type_lower = (question_type_raw || "").toLowerCase();
      return MULTISELECT_QUESTION_TYPES.includes(type_lower);
    },

    is_choice_selected(choice, selected_answers) {
      const answers_list = selected_answers || [];
      return answers_list.includes(choice);
    },

    // ─── File Preview ────────────────────────────────────────────

    open_file_preview(presigned_url, file_name) {
      if (!presigned_url) {
        this.show_error("No file URL available");
        return;
      }
      this.preview_file_url = presigned_url;
      this.preview_file_name = file_name || "File";
      this.show_file_preview = true;
    },

    close_file_preview() {
      this.show_file_preview = false;
      this.preview_file_url = "";
      this.preview_file_name = "";
    },

    get_file_name_from_url(url) {
      if (!url) return "File";

      const url_without_params = url.split("?")[0];
      const url_parts = url_without_params.split("/");
      const last_part = url_parts[url_parts.length - 1];

      try {
        return decodeURIComponent(last_part) || "File";
      } catch (decode_error) {
        return last_part || "File";
      }
    },

    // ─── Formatting ──────────────────────────────────────────────

    format_datetime(date_string) {
      if (!date_string) return "—";

      const date = new Date(date_string);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    truncate_text(text, max_length) {
      const limit = max_length || 100;
      if (!text) return "";

      if (text.length > limit) {
        return text.substring(0, limit) + "...";
      }
      return text;
    },
  },
};